import { getCommon } from '@/components/report/service';
import { CALCWEIGHT, Aggregator } from '@/models/GenerateCondit/Interrelated';
import moment from 'moment';
import { history } from 'umi';
import { crudRequest } from '@/services/api';
import { InitOrganizationAuth } from '@/services/area';
import { message } from 'antd';
import { getToDay } from '@/utils/date';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
const weekArray = ['第一周', '第二周', '第三周', '第四周'];
const dayArray = ['0', '-1', '-2', '-3', '-4', '-5', '-6'];

let [startDate] = getToDay();

/**
 * 使用BigNumber进行安全的数学运算
 * @param {Function} operation - 要执行的数学运算函数
 * @param {...number} args - 运算的参数
 * @returns {number} 运算结果
 */
function safeCalculation(operation, ...args) {
  const result = args.reduce((acc, curr) => {
    return operation(BigNumber(acc), BigNumber(curr));
  }, BigNumber(0));
  return result.toNumber();
}

/**
 * 安全的加法运算
 */
const safeAdd = (...args) => safeCalculation((a, b) => a.plus(b), ...args);

/**
 * 安全的除法运算
 */
const safeDivide = (a, b) => {
  const result = BigNumber(a).dividedBy(BigNumber(b).eq(0) ? 1 : b);
  return result.toNumber();
};

/**
 * 获取通用数据
 * @param {Object} params - 请求参数
 * @returns {Promise<Object|null>} 返回请求结果或null（如果出错）
 */
async function getCommonSr(params) {
  try {
    const srData = await getCommon(params, 'druid2');
    srData.data = srData.data?.map((item) => {
      return {
        ...item,
        druidDefaultSearchTime: moment(item.scanTime).valueOf(),
      };
    });
    return srData;
  } catch (error) {
    console.error('获取数据失败:', error);
    message.error('获取数据失败，请稍后重试');
    return null;
  }
}

export default {
  namespace: 'actualProductionRatioDetailsr',
  state: {
    onSearch: false,
    access: InitOrganizationAuth(),
    searchParams: {
      lineTime2: startDate.subtract(2, 'days').format('YYYY-MM-DD HH:mm:ss'),
      isStandard: '',
      transportLineType: '',
      volumesType: '',
      startDate: '',
      endDate: '',
      departureCenter: undefined,
      arrivalCenter: '',
      isWorkDay: ['工作日', '周六', '周日'],
      routeType: '营运区-营运区',
      areaProperty: '',
    },
    '/cost/planProductionRatioDetail': {
      access: InitOrganizationAuth(),
      dayTableDatas: [],
      weekTableDatas: [],
    },
    '/cost/actualProductionRatioDetailsr': {
      access: InitOrganizationAuth(),
      dayTableDatas: [],
      weekTableDatas: [],
    },
  },

  effects: {
    /**
     * 获取周累计值
     * @param {Object} payload - 请求参数
     * @param {Function} call - 用于调用异步函数
     * @param {Function} put - 用于触发 action
     * @param {Function} select - 用于从 state 中获取数据
     */
    *getWeekSumData({ payload }, { call, put, select }) {
      const {
        departureCenter,
        arrivalCenter,
        isWorkDay,
        transportLineType,
        volumesType,
        isStandard,
        startDate,
        endDate,
        routeType,
      } = payload;

      const PATHNAME = history?.location?.pathname;

      let actualProductionRatioDetailsr = yield select(
        ({ actualProductionRatioDetailsr }) => actualProductionRatioDetailsr,
      );

      const { access } = actualProductionRatioDetailsr;

      if (!access.isHead && departureCenter == null) {
        message.warn('请选择出发站点/营运区');
        return;
      }

      let goodsAndFilters = [];
      if (transportLineType) {
        goodsAndFilters.push({ key: 'goodsType', value: transportLineType });
      }

      let dimensions = ['monthType'];
      let directionKey = handleDirectionFilter(
        routeType,
        departureCenter,
        goodsAndFilters,
        arrivalCenter,
      );
      dimensions.push(...directionKey);

      let inFilters = [
        {
          key: 'monthType',
          value: [...weekArray],
        },
      ];
      if (isWorkDay && isWorkDay.length) {
        inFilters.push({ key: 'isWorkDay', value: isWorkDay });
      }

      const result = yield call(getCommonSr, {
        dataSource: 't_roi_goods_result',
        dimensions,
        aggregators: [CALCWEIGHT],
        simpleGranularity: 'day',
        startDate: startDate,
        endDate: endDate,
        andFilters: goodsAndFilters,
        inFilters: inFilters,
      });
      if (!result) {
        return;
      }

      let volumeAndFilters = [];
      if (volumesType && volumesType === '正班') {
        inFilters.push({
          key: 'volumesType',
          value: ['正班双边', '正班单边'],
        });
      }
      if (volumesType && volumesType === '加班') {
        volumeAndFilters.push({ key: 'volumesType', value: '临时加班' });
      }
      volumeAndFilters.push({
        key: 'volumeName',
        value: PATHNAME === '/cost/planProductionRatioDetail' ? 1 : 0,
      });
      if (isStandard) {
        volumeAndFilters.push({
          key: 'isStandard',
          value: isStandard === '基准线路' ? isStandard : '',
        });
      }

      if (transportLineType) {
        volumeAndFilters.push({ key: 'transportLineType', value: transportLineType });
      }

      let dimensions1 = ['monthType'];
      let directionKey1 = handleDirectionFilter(
        routeType,
        departureCenter,
        volumeAndFilters,
        arrivalCenter,
      );
      dimensions1.push(...directionKey1);

      const volumeResult = yield call(getCommonSr, {
        dataSource: 't_roi_volume_result',
        dimensions: dimensions1,
        simpleGranularity: 'day',
        startDate: startDate,
        endDate: endDate,
        andFilters: volumeAndFilters,
        inFilters: inFilters,
        aggregators: [new Aggregator('doubleSum', 'volumes', 'volumes')],
      });
      if (!volumeResult) {
        return;
      }

      let weekTableDatas = handleData(
        weekArray,
        result,
        volumeResult,
        (item) => item.monthType,
        directionKey,
      );

      let dayTableDatas = yield select(
        ({ actualProductionRatioDetailsr }) =>
          actualProductionRatioDetailsr?.[PATHNAME].dayTableDatas,
      );

      crudRequest(
        PATHNAME === '/cost/planProductionRatioDetail'
          ? '/api/route/planProductionRatioDetail/search_week'
          : '/api/route/actualProductionRatioDetail/search_week',
      );

      yield put({
        type: 'updateState',
        payload: {
          [PATHNAME]: { weekTableDatas, dayTableDatas },
        },
      });
    },

    /**
     * 获取单日值
     * @param {Object} _ - 未使用的参数
     * @param {Function} call - 用于调用异步函数
     * @param {Function} put - 用于触发 action
     * @param {Function} select - 用于从 state 中获取数据
     */
    *getDaySumData(_, { call, put, select }) {
      let actualProductionRatioDetailsr = yield select(
        ({ actualProductionRatioDetailsr }) => actualProductionRatioDetailsr,
      );

      const { access, searchParams } = actualProductionRatioDetailsr;

      const PATHNAME = history?.location?.pathname;
      const {
        departureCenter,
        arrivalCenter,
        transportLineType,
        volumesType,
        isStandard,
        routeType,
      } = searchParams;

      if (!access.isHead && departureCenter == null) {
        message.warn('请选择出发站点/营运区');
        return;
      }

      let startDate = moment(searchParams.lineTime2)
        .startOf('day')
        .subtract(6, 'days')
        .format('YYYY-MM-DD HH:mm:ss');
      let endDate = moment(searchParams.lineTime2).endOf('day').format('YYYY-MM-DD HH:mm:ss');

      let goodsAndFilters = [];
      if (transportLineType) {
        goodsAndFilters.push({ key: 'goodsType', value: transportLineType });
      }

      let dimensions = ['scanTime'];
      let directionKey = handleDirectionFilter(
        routeType,
        departureCenter,
        goodsAndFilters,
        arrivalCenter,
      );
      dimensions.push(...directionKey);

      let inFilters = [];

      // 请求结算重量数据
      const result = yield call(getCommonSr, {
        dimensions,
        simpleGranularity: 'day',
        startDate: startDate,
        endDate: endDate,
        dataSource: 't_roi_goods_result',
        andFilters: goodsAndFilters,
        inFilters,
        aggregators: [CALCWEIGHT],
      });
      if (!result) {
        return;
      }

      let volumeAndFilters = [];
      if (volumesType && volumesType === '正班') {
        inFilters.push({
          key: 'volumesType',
          value: ['正班双边', '正班单边'],
        });
      }
      if (volumesType && volumesType === '加班') {
        volumeAndFilters.push({ key: 'volumesType', value: '临时加班' });
      }
      if (isStandard) {
        volumeAndFilters.push({
          key: 'isStandard',
          value: isStandard === '基准线路' ? isStandard : '',
        });
      }
      if (transportLineType) {
        volumeAndFilters.push({ key: 'transportLineType', value: transportLineType });
      }
      volumeAndFilters.push({
        key: 'volumeName',
        value: PATHNAME === '/cost/planProductionRatioDetail' ? 1 : 0,
      });

      let dimensions1 = ['scanTime'];
      let directionKey1 = handleDirectionFilter(
        routeType,
        departureCenter,
        volumeAndFilters,
        arrivalCenter,
      );
      dimensions1.push(...directionKey1);
      // 请求实际运力数据
      const volumeResult = yield call(getCommonSr, {
        dimensions: dimensions1,
        simpleGranularity: 'day',
        startDate: startDate,
        endDate: endDate,
        aggregators: [new Aggregator('doubleSum', 'volumes', 'volumes')],
        dataSource: 't_roi_volume_result',
        andFilters: volumeAndFilters,
        inFilters: inFilters,
      });
      if (!volumeResult) {
        return;
      }

      let dayTableDatas = handleData(
        dayArray,
        result,
        volumeResult,
        (item) => moment(item.druidDefaultSearchTime).diff(endDate, 'days'),
        directionKey,
      );

      console.log('[dayTableDatas]', dayTableDatas);

      let weekTableDatas = yield select(
        ({ actualProductionRatioDetailsr }) =>
          actualProductionRatioDetailsr?.[PATHNAME].weekTableDatas,
      );

      crudRequest(
        PATHNAME === '/cost/planProductionRatioDetail'
          ? '/api/route/planProductionRatioDetail/search_day'
          : '/api/route/actualProductionRatioDetail/search_day',
      );

      yield put({
        type: 'updateState',
        payload: {
          [PATHNAME]: { dayTableDatas, weekTableDatas },
        },
      });
    },
  },

  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    updateSearchParams(state, { payload }) {
      return { ...state, searchParams: { ...state.searchParams, ...payload } };
    },
  },
};

/**
 * 处理方向过滤器
 * @param {string} routeType - 路线类型
 * @param {Object} departureCenter - 出发中心
 * @param {Array} goodsAndFilters - 商品和过滤器数组
 * @param {Object} arrivalCenter - 到达中心
 * @returns {Array} 方向键数组
 */
function handleDirectionFilter(routeType, departureCenter, goodsAndFilters, arrivalCenter) {
  let directionKey = [];
  if (routeType === '营运区-营运区') {
    if (departureCenter) {
      goodsAndFilters.push({ key: 'departureProvinceAreaCode', value: departureCenter?.code });
    }
    if (arrivalCenter) {
      goodsAndFilters.push({ key: 'arrivalProvinceAreaCode', value: arrivalCenter?.code });
    }
    directionKey.push('departureProvinceArea', 'arrivalProvinceArea');
  } else if (routeType === '营运区-分拨') {
    if (departureCenter) {
      goodsAndFilters.push({ key: 'departureProvinceAreaCode', value: departureCenter?.code });
    }
    if (arrivalCenter) {
      goodsAndFilters.push({ key: 'arrivalCenterCode', value: arrivalCenter?.code });
    }
    directionKey.push('departureProvinceArea', 'arrivalCenter');
  } else if (routeType === '分拨-营运区') {
    if (departureCenter) {
      goodsAndFilters.push({ key: 'departureCenterCode', value: departureCenter?.code });
    }
    if (arrivalCenter) {
      goodsAndFilters.push({ key: 'arrivalProvinceAreaCode', value: arrivalCenter?.code });
    }
    directionKey.push('departureCenter', 'arrivalProvinceArea');
  } else if (routeType === '分拨-分拨') {
    if (departureCenter) {
      goodsAndFilters.push({ key: 'departureCenterCode', value: departureCenter?.code });
    }
    if (arrivalCenter) {
      goodsAndFilters.push({ key: 'arrivalCenterCode', value: arrivalCenter?.code });
    }
    directionKey.push('departureCenter', 'arrivalCenter');
  }

  return directionKey;
}

/**
 * 处理数据
 * @param {Array} columeArray - 列数组
 * @param {Object} goodsResult - 商品结果
 * @param {Object} volumeResult - 体积结果
 * @param {Function} columnName - 列名函数
 * @param {Array} directionKey - 方向键数组
 * @returns {Array} 处理后的表格数据
 */
function handleData(columeArray, goodsResult, volumeResult, columnName, directionKey) {
  try {
    let allCountInit = columeArray.reduce((pre, current) => {
      pre[current] = 0;
      return pre;
    }, {});
    const parseJsonSafely = (data) => {
      let parsedData;
      if (Array.isArray(data)) {
        parsedData = data;
      } else if (typeof data === 'object' && data !== null) {
        parsedData = [data];
      } else {
        try {
          parsedData = JSON.parse(data || '[]');
        } catch (error) {
          console.error('JSON解析错误:', error);
          parsedData = [];
        }
      }

      const processedData = parsedData.map((item) => ({
        ...item,
        calcWeight: Number(item?.calcWeight),
        volumes: Number(item?.volumes),
        scanTime: item.scanTime, // 确保scanTime字段存在
      }));

      return _.orderBy(processedData, ['scanTime'], ['asc']);
    };

    const goodsData = parseJsonSafely(goodsResult.data).reduce(
      (pre, current) => {
        let direction = `${current[directionKey[0]]}-${current[directionKey[1]]}`;
        let name = columnName(current);

        if (pre[direction]) {
          pre[direction][name] = pre[direction][name] + current.calcWeight;
        } else {
          pre[direction] = { ...allCountInit, [name]: current.calcWeight };
        }

        pre['合计'] = {
          ...pre['合计'],
          [name]: (pre['合计']?.[name] || 0) + current.calcWeight,
        };

        return pre;
      },
      { 合计: { ...allCountInit } },
    );

    const volumeData = parseJsonSafely(volumeResult.data).reduce(
      (pre, current) => {
        let name = columnName(current);
        let direction = `${current[directionKey[0]]}-${current[directionKey[1]]}`;
        if (pre[direction]) {
          pre[direction][name] = pre[direction][name] + current.volumes;
        } else {
          pre[direction] = { ...allCountInit, [name]: current.volumes };
        }
        pre['合计'] = {
          ...pre['合计'],
          [name]: (pre['合计']?.[name] || 0) + current.volumes,
        };
        return pre;
      },
      { 合计: { ...allCountInit } },
    );

    let sumResult = columeArray.reduce((pre, current) => {
      pre[`${current}Goods`] = goodsData['合计']?.[current] || 0;
      pre[`${current}Volume`] = volumeData['合计']?.[current] || 0;
      pre[current] =
        pre[`${current}Volume`] !== 0
          ? pre[`${current}Goods`] / pre[`${current}Volume`] / 1000
          : ''; // 0% 就是
      return pre;
    }, {});

    let monthSumResult = columeArray.reduce(
      (pre, current) => {
        pre['calcWeight'] += goodsData['合计']?.[current] || 0;
        pre['volumes'] += volumeData['合计']?.[current] || 0;
        return pre;
      },
      { calcWeight: 0, volumes: 0 },
    );

    let tableDatas = [
      {
        direction: '合计',
        ...sumResult,
        totalGoods: monthSumResult.calcWeight,
        totalVolume: monthSumResult.volumes,
        month:
          monthSumResult.volumes !== 0
            ? monthSumResult.calcWeight / monthSumResult.volumes / 1000
            : '', // 0% 就是 -
      },
    ];

    Object.entries(goodsData).forEach(([key, value]) => {
      if (key === '合计') {
        return;
      }
      let weekResult = columeArray.reduce((pre, current) => {
        pre[`${current}Goods`] = value?.[current] || 0;
        pre[`${current}Volume`] = volumeData[key]?.[current] || 0;
        if (!pre['totalGoods']) {
          pre['totalGoods'] = 0;
        }
        if (!pre['totalVolume']) {
          pre['totalVolume'] = 0;
        }
        pre['totalGoods'] += value?.[current] || 0;
        pre['totalVolume'] += volumeData[key]?.[current] || 0;
        pre[current] =
          pre[`${current}Volume`] !== 0
            ? pre[`${current}Goods`] / pre[`${current}Volume`] / 1000
            : ''; // 0% 就是 -
        return pre;
      }, {});

      weekResult['month'] =
        weekResult['totalVolume'] !== 0
          ? weekResult['totalGoods'] / weekResult['totalVolume'] / 1000
          : ''; // 0% 就是 -

      if (value && volumeData[key]) {
        tableDatas.push({
          direction: key,
          ...weekResult,
          month:
            Object.values(value).reduce((pre, cur) => pre + cur, 0) /
            Object.values(volumeData[key]).reduce((pre, cur) => pre + cur, 0) /
            1000,
        });
      }
    });
    return tableDatas;
  } catch (error) {
    console.error('处理数据时出现错误:', error);
    return [];
  }
}
